import { Check, LoaderCircle } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { UserAccountSummary } from "../../services/userAccountContextService";
import type { HttpError } from "../../services/httpService";
import { isInternalUser } from "../../utils/internalUsers";

interface AlphaAccountContextListProps {
  accounts: UserAccountSummary[];
  actorUserId: string | null;
  effectiveUserId: string | null;
  isLoading: boolean;
  isSwitching: boolean;
  appearance?: "light" | "dark";
  autoFocus?: boolean;
  onActivate: (targetUserId: string) => Promise<void>;
  onActivated?: () => void;
  onEscape?: () => void;
}

function getHttpStatus(error: unknown): number | undefined {
  const httpError = error as HttpError;
  return httpError.status ?? httpError.response?.status;
}

export function getAccountActivationError(error: unknown): string {
  const status = getHttpStatus(error);
  if (status === 401) {
    return "Your session expired. Sign in again before switching accounts.";
  }
  if (status === 403 || status === 404) {
    return "That account is no longer available. Your current account is unchanged. Refresh the page to update your account choices.";
  }
  return "We could not switch accounts. Your current account is unchanged. Try again.";
}

interface AccountDisplayFields {
  name: string;
  externalEmail: string;
  ibkrAccountId: string;
}

function getAccountDisplayFields(account: UserAccountSummary): AccountDisplayFields {
  const rawName = account.name?.trim() || "";
  const email = account.email.trim();
  return {
    name: rawName && !isInternalUser(rawName) ? rawName : "",
    externalEmail: email && !isInternalUser(email) ? email : "",
    ibkrAccountId: account.ibkrAccountId?.trim() || "",
  };
}

export function getAccountDisplayName(account: UserAccountSummary): string {
  const { name, externalEmail, ibkrAccountId } = getAccountDisplayFields(account);
  return name || externalEmail || ibkrAccountId;
}

const AlphaAccountContextList: React.FC<AlphaAccountContextListProps> = ({
  accounts,
  actorUserId,
  effectiveUserId,
  isLoading,
  isSwitching,
  appearance = "light",
  autoFocus = false,
  onActivate,
  onActivated,
  onEscape,
}) => {
  const isDark = appearance === "dark";
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const actionGuardRef = useRef(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const displayableAccounts = useMemo(() => accounts
    .map((account) => ({ account, ...getAccountDisplayFields(account) }))
    .filter(({ account, name, externalEmail, ibkrAccountId }) =>
      Boolean(name || externalEmail || ibkrAccountId)
      || account.userId === actorUserId
    ), [accounts, actorUserId]);

  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, displayableAccounts.length);
  }, [displayableAccounts.length]);

  useEffect(() => {
    if (!autoFocus || isLoading || displayableAccounts.length === 0) return;
    const selectedIndex = displayableAccounts.findIndex(
      ({ account }) => account.userId === effectiveUserId
    );
    optionRefs.current[selectedIndex >= 0 ? selectedIndex : 0]?.focus();
  }, [autoFocus, displayableAccounts, effectiveUserId, isLoading]);

  const moveFocus = (currentIndex: number, direction: 1 | -1): void => {
    if (displayableAccounts.length === 0) return;
    const nextIndex = (
      currentIndex + direction + displayableAccounts.length
    ) % displayableAccounts.length;
    optionRefs.current[nextIndex]?.focus();
  };

  const handleActivate = async (account: UserAccountSummary): Promise<void> => {
    if (
      actionGuardRef.current
      || isSwitching
      || account.userId === effectiveUserId
    ) return;

    actionGuardRef.current = true;
    setPendingUserId(account.userId);
    setError(null);
    try {
      await onActivate(account.userId);
      onActivated?.();
    } catch (activationError) {
      setError(getAccountActivationError(activationError));
    } finally {
      actionGuardRef.current = false;
      setPendingUserId(null);
    }
  };

  if (isLoading && accounts.length === 0) {
    return (
      <div
        role="status"
        className={`flex min-h-24 items-center justify-center gap-2 rounded-v7-control border text-sm ${
          isDark
            ? "border-v7-white/20 bg-v7-white/5 text-v7-white/75"
            : "border-v7-ink/15 bg-v7-paper text-v7-ink-soft"
        }`}
      >
        <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading accounts
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div
        role="alert"
        className={`rounded-v7-control border p-3 text-sm ${
          isDark
            ? "border-v7-white/20 bg-v7-white/5 text-v7-white/80"
            : "border-v7-rust/30 bg-v7-rust/5 text-v7-rust"
        }`}
      >
        Account choices are unavailable. Refresh the page to try again.
      </div>
    );
  }

  if (displayableAccounts.length === 0) {
    return (
      <div
        role="status"
        className={`rounded-v7-control border p-3 text-sm ${
          isDark
            ? "border-v7-white/20 bg-v7-white/5 text-v7-white/80"
            : "border-v7-ink/15 bg-v7-paper text-v7-ink-soft"
        }`}
      >
        No account details are available.
      </div>
    );
  }

  return (
    <div className="ph-mask space-y-2">
      <div role="menu" aria-label="Available accounts" className="grid gap-1.5">
        {displayableAccounts.map(({
          account,
          name,
          externalEmail,
          ibkrAccountId,
        }, index) => {
          const isCurrent = account.userId === effectiveUserId;
          const isSelf = account.userId === actorUserId;
          const usesSelfFallback = isSelf
            && !name
            && !externalEmail
            && !ibkrAccountId;
          const displayName = name
            || externalEmail
            || ibkrAccountId
            || "Your account";
          const primaryLabel = name
            || externalEmail
            || (ibkrAccountId ? `IBKR ${ibkrAccountId}` : "Your account");
          const isPending = pendingUserId === account.userId;
          return (
            <button
              key={account.userId}
              ref={(element) => {
                optionRefs.current[index] = element;
              }}
              type="button"
              role="menuitemradio"
              aria-checked={isCurrent}
              aria-disabled={isCurrent || isSwitching || pendingUserId !== null}
              disabled={(isSwitching || pendingUserId !== null) && !isCurrent}
              onClick={() => void handleActivate(account)}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  moveFocus(index, 1);
                } else if (event.key === "ArrowUp") {
                  event.preventDefault();
                  moveFocus(index, -1);
                } else if (event.key === "Home") {
                  event.preventDefault();
                  optionRefs.current[0]?.focus();
                } else if (event.key === "End") {
                  event.preventDefault();
                  optionRefs.current[displayableAccounts.length - 1]?.focus();
                } else if (event.key === "Escape" && onEscape) {
                  event.preventDefault();
                  onEscape();
                }
              }}
              className={`flex min-h-14 w-full items-center gap-3 rounded-v7-control border px-3 py-2 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze ${
                isCurrent
                  ? isDark
                    ? "border-v7-bronze bg-v7-bronze/15 text-v7-white"
                    : "border-v7-bronze/50 bg-v7-bronze/10 text-v7-ink"
                  : isDark
                    ? "border-v7-white/15 text-v7-white/85 hover:border-v7-white/30 hover:bg-v7-white/10"
                    : "border-v7-ink/15 bg-v7-white text-v7-ink hover:border-v7-ink/30 hover:bg-v7-paper"
              } disabled:cursor-wait disabled:opacity-60`}
            >
              <span
                aria-hidden="true"
                className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-semibold ${
                  isCurrent
                    ? "bg-v7-bronze-dark text-v7-white"
                    : isDark
                      ? "bg-v7-white/10 text-v7-white"
                      : "bg-v7-paper text-v7-ink"
                }`}
              >
                {displayName.charAt(0).toUpperCase() || "?"}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className={`truncate text-sm ${name ? "font-bold" : "font-semibold"}`}>
                    {primaryLabel}
                  </span>
                  {isSelf && !usesSelfFallback && (
                    <span className={`rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
                      isDark
                        ? "border-v7-white/20 text-v7-white/70"
                        : "border-v7-ink/15 text-v7-ink-soft"
                    }`}>
                      Your account
                    </span>
                  )}
                </span>
                {name && externalEmail && (
                  <span className={`mt-0.5 block truncate text-xs ${
                    isDark ? "text-v7-white/65" : "text-v7-ink/60"
                  }`}>
                    {externalEmail}
                  </span>
                )}
                {ibkrAccountId && (name || externalEmail) && (
                  <span className={`mt-0.5 block truncate text-[11px] ${
                    isDark ? "text-v7-white/55" : "text-v7-ink/50"
                  }`}>
                    IBKR {ibkrAccountId}
                  </span>
                )}
              </span>
              <span className="flex shrink-0 items-center gap-1 text-[10px] font-semibold uppercase tracking-wider">
                {isPending ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : isCurrent ? (
                  <>
                    <Check className="h-4 w-4" aria-hidden="true" />
                    Current
                  </>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      {error && (
        <p
          role="alert"
          className={`rounded-v7-control border p-2.5 text-xs leading-5 ${
            isDark
              ? "border-v7-rust/50 bg-v7-rust/15 text-v7-white"
              : "border-v7-rust/30 bg-v7-rust/5 text-v7-rust"
          }`}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default AlphaAccountContextList;
